import { v4 as uuidv4 } from 'uuid';

const IP_SESSION_STORAGE_KEY = 'client_ip';
const IPIFY_URL = 'https://api.ipify.org';
const JITSI_LOCAL_STORAGE_KEY = 'jitsiLocalStorage';
// Marks a synthetic jitsiMeetId when retrieveJitsiMeetId() rejects. Lets observability
// queries distinguish real ids from fallbacks (e.g. WHERE device_id LIKE 'ffffffff-%').
const FALLBACK_JITSI_MEET_ID_PREFIX = 'ffffffff-';

let cachedJitsiMeetId: string | undefined;
let cachedCallStatsUserName: string | undefined;
let cachedClientIp: string | undefined;

export async function initClientMetadata(jitsiMeetIdPromise: Promise<string>): Promise<void> {
  await Promise.all([initClientIp(), initJitsiMeetId(jitsiMeetIdPromise)]);
  refreshCallStatsUserName();
}

export function refreshCallStatsUserName(): void {
  const value = readJitsiLocalStorageField('callStatsUserName');
  if (value) {
    cachedCallStatsUserName = value;
  }
}

interface AttributeTarget {
  setAttribute(key: string, value: string): unknown;
}

export function applyClientMetadata(target: AttributeTarget): void {
  if (cachedJitsiMeetId) {
    // jitsiMeetId is the jitsi device id — un-namespaced as device_id to match
    // the backend, which already uses that identifier.
    target.setAttribute('device_id', cachedJitsiMeetId);
  }
  if (cachedCallStatsUserName) {
    target.setAttribute('jitsi.call_stats_user_name', cachedCallStatsUserName);
  }
  if (cachedClientIp) {
    target.setAttribute('client.ip', cachedClientIp);
  }
}

async function initClientIp(): Promise<void> {
  const existing = readSessionStorage(IP_SESSION_STORAGE_KEY);
  if (existing) {
    cachedClientIp = existing;
    return;
  }
  try {
    const response = await fetch(IPIFY_URL);
    if (!response.ok) {
      console.warn(`[clientMetadata] ipify returned ${response.status}`);
      return;
    }
    const ip = (await response.text()).trim();
    if (ip) {
      sessionStorage.setItem(IP_SESSION_STORAGE_KEY, ip);
      cachedClientIp = ip;
    }
  } catch (error) {
    console.warn('[clientMetadata] failed to fetch client IP:', error);
  }
}

async function initJitsiMeetId(jitsiMeetIdPromise: Promise<string>): Promise<void> {
  cachedJitsiMeetId = await jitsiMeetIdPromise.catch((error) => {
    const fallback = `${FALLBACK_JITSI_MEET_ID_PREFIX}${uuidv4().replace(/-/g, '')}`;
    console.warn(`[clientMetadata] retrieveJitsiMeetId failed, using fallback: ${fallback}`, error);
    return fallback;
  });
}

function readSessionStorage(key: string): string | undefined {
  try {
    return sessionStorage.getItem(key) ?? undefined;
  } catch {
    return undefined;
  }
}

function readJitsiLocalStorageField(field: string): string | undefined {
  try {
    const raw = localStorage.getItem(JITSI_LOCAL_STORAGE_KEY);
    if (!raw) {
      return undefined;
    }
    const parsed = JSON.parse(raw);
    const value = parsed?.[field];
    return typeof value === 'string' ? value : undefined;
  } catch {
    return undefined;
  }
}
